import { useLanguage } from '../utils/LanguageContext';
import { ValidatedForm, FormSubmit, FormChoice, FormInput } from './common/ValidatedForm';
import { updateUserResidencyStatusClientSchema } from '../shared/validations/userValidations';
import translations from './ComponentsTranslations.lang.json';
import { useAuth } from '../utils/AuthContext.js';
import { ResidencyStatus, Country, VisaType } from '../shared/constants/userConstants.js';

const { CITIZEN, PERMANENT_RESIDENT, VISA_HOLDER } = ResidencyStatus;

// TODO: UI improvements for this form:
// 1) prioritize likely countries on top of the list?, e.g. USA for country of birth?
// 2) make country of birth match country of citizenship as a default, or make that option first?
// 3) sort countries by name alphabetically by current language

const ResidencyStatusForm = ({ onFinish }) => {
  const { lang, optionsForEnum } = useLanguage(translations);
  const { user, api } = useAuth();

  const initialValues = {
    residencyStatus: {
      residencyStatusType:
        user.residencyStatus?.countryOfCitizenship === 'USA'
          ? CITIZEN
          : user.residencyStatus?.isUSPermanentResident === true
          ? PERMANENT_RESIDENT
          : user.residencyStatus?.isUSPermanentResident === false
          ? VISA_HOLDER
          : null,
      countryOfCitizenship: user.residencyStatus?.countryOfCitizenship,
      countryOfBirth: user.residencyStatus?.countryOfBirth,
      visaExpirationDate: user.residencyStatus?.visaExpirationDate,
      visaType: user.residencyStatus?.visaType,
      dateOfDepartureFromUS: user.residencyStatus?.dateOfDepartureFromUS,
    },
  };

  const preValidation = ({ watch, setValue, clearValues }) => {
    const residencyStatusType = watch('residencyStatus.residencyStatusType');

    const visaFields = [
      'residencyStatus.visaExpirationDate',
      'residencyStatus.visaType',
      'residencyStatus.dateOfDepartureFromUS',
    ];
    const visaOrPermanentResidentFields = [
      'residencyStatus.isUSPermanentResident',
      'residencyStatus.countryOfBirth',
      ...visaFields,
    ];

    if (residencyStatusType === CITIZEN) {
      setValue('residencyStatus.countryOfCitizenship', 'USA');
      clearValues(visaOrPermanentResidentFields);
    } else if (residencyStatusType === PERMANENT_RESIDENT) {
      setValue('residencyStatus.isUSPermanentResident', true);
      clearValues(visaFields);
    } else if (residencyStatusType === VISA_HOLDER) {
      setValue('residencyStatus.isUSPermanentResident', false);
    }
  };

  const handleSubmit = async (formInput) => {
    //strip out the residencyStatusType from the form input
    const { residencyStatusType, ...residencyStatus } = formInput.residencyStatus;
    await api.updateUserResidencyStatus({ residencyStatus });
    await onFinish({ residencyStatus });
  };

  const emptyIfUSA = (value) => (value === 'USA' ? undefined : value);

  return (
    <ValidatedForm
      key="residencyStatusForm"
      schema={updateUserResidencyStatusClientSchema}
      initialValues={initialValues}
      preValidation={preValidation}
      handleSubmit={handleSubmit}
      onFinish={onFinish}
    >
      <FormChoice name="residencyStatus.residencyStatusType" options={optionsForEnum(ResidencyStatus)} type="radio" />
      {({ watch }) =>
        [PERMANENT_RESIDENT, VISA_HOLDER].includes(watch('residencyStatus.residencyStatusType')) && (
          <>
            <FormChoice
              name="residencyStatus.countryOfCitizenship"
              value={emptyIfUSA(watch('residencyStatus.countryOfCitizenship'))}
              options={optionsForEnum(Country).filter((o) => o.value !== 'USA')}
              type="select"
            />
            <FormChoice name="residencyStatus.countryOfBirth" options={optionsForEnum(Country)} type="select" />
            {VISA_HOLDER === watch('residencyStatus.residencyStatusType') && (
              <>
                <FormInput name="residencyStatus.visaExpirationDate" type="date" />
                <FormChoice name="residencyStatus.visaType" options={optionsForEnum(VisaType)} type="select" />
                {[VisaType.B1, VisaType.B2].includes(watch('residencyStatus.visaType')) && (
                  <FormInput name="residencyStatus.dateOfDepartureFromUS" type="date" />
                )}
              </>
            )}
          </>
        )
      }
      <FormSubmit>{lang('next')}</FormSubmit>
    </ValidatedForm>
  );
};

export default ResidencyStatusForm;
